/**
 * The conversation-scoped busy view — what a Project Chat's agents are doing,
 * answered for EVERY member of the room, not only the agents' owner.
 *
 * The owner's own halo (agents/everywhere.ts) asks their OWN machines; a friend
 * cannot ask someone else's machine anything. So this asks the BFF, which checks
 * conversation membership and relays the same /agent/idle_status question to each
 * agent's hosting machine over the link it already holds — the same source of
 * truth, one authorization (membership) further out.
 *
 * LIVES IN src/servers ON PURPOSE: this file talks to the BFF's /account routes,
 * which authenticate by their own session cookie + Origin check — the edge's
 * CSRF token has no meaning there (csrfCoverage.test.ts states the exemption).
 */

/** One row of POST /account/chat/conv_busy. */
export type ConvBusyAgent = {
  hash: string
  /** 'active' = working right now; 'idle'; 'off' = its machine could not be asked. */
  status: string
  session?: string
  host?: string | null
  name?: string
  owner_user?: string
  mode?: string
}

export const getConvBusy = async (convId: string): Promise<{ agents: ConvBusyAgent[] }> => {
  try {
    const res = await fetch('/account/chat/conv_busy', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ conv_id: convId }),
    })
    // A refusal (not a member, chat lane down) and a transport failure both
    // degrade the same way for the float: no conversation-scoped rows. The
    // owner's own agents still light from their direct path.
    if (!res.ok) return { agents: [] }
    const data = (await res.json().catch(() => null)) as { agents?: ConvBusyAgent[] } | null
    return { agents: data?.agents ?? [] }
  } catch {
    return { agents: [] }
  }
}
